import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import PhotoCamera from '@mui/icons-material/PhotoCamera'
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Portal,
  Radio,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { FieldArray, Form, Formik } from 'formik'
import {
  assign,
  compact,
  filter,
  find,
  flatMap,
  get,
  includes,
  isEmpty,
  isNull,
  map,
  omit,
  some,
  spread,
} from 'lodash'

import { BodyCondition, Dictionaries } from '../api/index'
import ButtonBox from '../components/ButtonBox'
import ImageModal from '../components/ImageModal'
import Section from '../components/Section'
import SectionHeader from '../components/SectionHeader'
import useAlert from '../hooks/useAlert'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import {
  ArrowLeft,
  ArrowRight,
  BodyIcon,
  CancelIcon,
  CheckIcon,
  DeleteIcon,
  SaveIcon,
} from '../icons'

const MIN_VALUE = 50

const isOptionSelected = (excludeItems, value) => {
  const excludeIds = compact(
    map(excludeItems, (item) =>
      item.bodyDamageId !== 1 ? item.bodyDamageId : null,
    ),
  )
  return includes(excludeIds, value)
}

const mergeByProperty = (target, source, prop) =>
  source.map((sourceElement) => {
    const targetElement = find(target, (t) => sourceElement[prop] === t[prop])
    return targetElement
      ? assign(targetElement, sourceElement)
      : {
          ...sourceElement,
          bodyDamages: [],
          bodyPaintWorkThickness: null,
        }
  })

function ControlsEditBodyCondition() {
  const {
    controlId: digitalCertificateId,
    portalRef,
    onCancelClick,
  } = useOutletContext()
  const { t } = useTranslation()
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const { setAlert } = useAlert()
  const [bodyDamageDictionary, setBodyDamageDictionary] = useState([])
  const [initialValues, setInitialValues] = useState({
    digitalCertificateId: '',
    bodyConditions: [],
  })
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: '',
    title: '',
  })

  useEffect(() => {
    Promise.all([
      Dictionaries.getBodyElementsDictionary(axiosPrivate),
      Dictionaries.getBodyDamageDictionary(axiosPrivate),
      BodyCondition.getBodyCondition(axiosPrivate, digitalCertificateId),
    ])
      .then(
        spread((_elements, _damages, _values) => {
          const newElements = map(
            _elements.data,
            ({ name, value, hasPaintworkThickness }) => ({
              bodyElementName: name,
              bodyElementId: value,
              hasPaintworkThickness,
            }),
          )

          setBodyDamageDictionary(_damages.data)
          setInitialValues({
            digitalCertificateId,
            bodyConditions: map(
              mergeByProperty(_values.data, newElements, 'bodyElementId'),
              (elem) => ({
                ...omit(elem, [
                  'bodyPaintWorkThicknessDescription',
                  'bodyPaintWorkThicknessDictId',
                ]),
                bodyDamages: isEmpty(elem.bodyDamages)
                  ? [
                      {
                        bodyDamageId: 1,
                        isMain: true,
                        image: null,
                      },
                    ]
                  : elem.bodyDamages,
              }),
            ),
          })
        }),
      )
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e))
  }, [axiosPrivate, digitalCertificateId])

  const checkImagesWithNoDesc = (values) => {
    const imagesWithNoDescArr = map(
      values,
      ({ bodyElementName, bodyDamages }) =>
        map(bodyDamages, (damage) => {
          if (
            get(damage, 'bodyDamageId', 1) === 1 &&
            get(damage, 'image', null)
          ) {
            return bodyElementName
          }
          return null
        }),
    )
    const imageWithNoDesc = find(flatMap(imagesWithNoDescArr))
    return imageWithNoDesc
  }

  const filterUnnecessaryDamages = (damages) => {
    const filteredDamages = filter(
      damages,
      (el) => get(el, 'bodyDamageId', 1) !== 1,
    )
    const noDamages = filteredDamages.length === 0

    if (noDamages) {
      return [
        {
          bodyDamageId: 1,
          isMain: true,
          image: null,
        },
      ]
    }

    const hasMainImage = some(
      filteredDamages,
      (el) => get(el, 'isMain') === true,
    )

    if (!hasMainImage) {
      filteredDamages[0].isMain = true
    }

    return filteredDamages
  }

  const saveBodyCondition = ({
    redirect,
    values,
    submitForm,
    setSubmitting,
  }) => {
    const payload = {
      ...values,
      bodyConditions: map(
        values.bodyConditions,
        ({ bodyPaintWorkThickness, bodyDamages, bodyElementId }) => ({
          bodyPaintWorkThickness,
          bodyElementId,
          bodyDamages: map(filterUnnecessaryDamages(bodyDamages), (damage) => ({
            ...damage,
            imageId: damage.image?.id || null,
          })),
        }),
      ),
    }

    const imageError = checkImagesWithNoDesc(values.bodyConditions)

    if (imageError) {
      setAlert(
        `Wybierz rodzaj uszkodzenia dla zdjęcia w sekcji ${imageError}`,
        'error',
      )
    } else {
      submitForm()
      BodyCondition.saveBodyCondition(
        axiosPrivate,
        digitalCertificateId,
        payload,
      )
        .then(() => {
          if (redirect) {
            navigate(redirect)
          }
        })
        // eslint-disable-next-line no-console
        .catch((e) => console.error(e))
        .finally(() => {
          setSubmitting(false)
        })
    }
  }

  const saveBodyConditionImage = (bodyElementId, setFieldValue) => (e) => {
    const fileForm = new FormData()
    fileForm.append('file', e.target.files[0])
    BodyCondition.saveBodyConditionImage(
      axiosPrivate,
      digitalCertificateId,
      bodyElementId,
      fileForm,
    )
      .then(({ data: { data } }) => {
        setFieldValue(e.target.name, data)
      })
      // eslint-disable-next-line no-shadow, no-console
      .catch((e) => console.error(e))
  }

  const deleteBodyConditionImage = (name, setFieldValue) => () => {
    setFieldValue(name, null)
  }

  const handleImageModalOpen = (title, imageUrl) => () => {
    setImageModal({ isOpen: true, imageUrl, title })
  }

  const handleImageModalClose = () => {
    setImageModal({ isOpen: false, imageUrl: '', title: '' })
  }

  const handlePaintworkvalidValue = (setFieldValue) => (e) => {
    if (e.target.value < MIN_VALUE) {
      setFieldValue(e.target.name, MIN_VALUE)
    } else {
      setFieldValue(e.target.name, Math.round(e.target.value))
    }
  }

  return (
    <div>
      <Section>
        <SectionHeader
          icon={<BodyIcon />}
          title="Opis stanu nadwozia"
          subtitle="Opisz wszystkie uszkodzenia występujące na karoserii pojazdu zgodnie z poniższym formularzem"
        />
        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={() => {}}
        >
          {({
            isSubmitting,
            values,
            handleChange,
            setSubmitting,
            setFieldValue,
            submitForm,
          }) => (
            <Form>
              <Box>
                <FieldArray name="bodyConditions">
                  <>
                    {map(values.bodyConditions, (element, elementIndex) => (
                      <Paper
                        key={element.bodyElementId}
                        elevation={0}
                        sx={{ mb: 2, p: 2 }}
                      >
                        <Typography
                          variant="h4"
                          gutterBottom
                          sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            backgroundColor: '#fff',
                          }}
                        >
                          {element.bodyElementName}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={3}>
                            {values.bodyConditions[elementIndex]
                              .hasPaintworkThickness && (
                              <>
                                <Typography variant="caption" gutterBottom>
                                  {t('bodyCondition.paintwork')}
                                </Typography>
                                <TextField
                                  name={`bodyConditions[${elementIndex}].bodyPaintWorkThickness`}
                                  disabled={
                                    !values.bodyConditions[elementIndex]
                                      .hasPaintworkThickness
                                  }
                                  value={
                                    values.bodyConditions[elementIndex]
                                      .bodyPaintWorkThickness || ''
                                  }
                                  type="number"
                                  onChange={handleChange}
                                  onBlur={handlePaintworkvalidValue(
                                    setFieldValue,
                                  )}
                                  fullWidth
                                  InputProps={{
                                    inputProps: { min: MIN_VALUE, step: 1 },
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        {t('bodyCondition.paintworkUnit')}
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </>
                            )}
                          </Grid>
                          <Grid item xs={12} md={9}>
                            <FieldArray
                              name={`bodyConditions[${elementIndex}].bodyDamages`}
                            >
                              {(arrayHelpers) => {
                                const handleMainDamageChange = (e) => {
                                  e.preventDefault()

                                  get(values, arrayHelpers.name).forEach(
                                    (_, key) => {
                                      setFieldValue(
                                        `${arrayHelpers.name}[${key}].isMain`,
                                        false,
                                      )
                                    },
                                  )
                                  setFieldValue(e.target.name, true)
                                }
                                const handleDamageRemove = (index) => () => {
                                  arrayHelpers.remove(index)
                                  const bodyDamagesArray = get(
                                    values,
                                    arrayHelpers.name,
                                  )
                                  const deletedItem = bodyDamagesArray[index]
                                  if (bodyDamagesArray.length === 1) {
                                    arrayHelpers.push({
                                      bodyDamageId: 1,
                                      isMain: true,
                                      image: null,
                                    })
                                  } else {
                                    const newBodyDamages = filter(
                                      bodyDamagesArray,
                                      (o) =>
                                        o.bodyDamageId !==
                                        deletedItem.bodyDamageId,
                                    )
                                    if (
                                      !find(newBodyDamages, { isMain: true })
                                    ) {
                                      setFieldValue(
                                        arrayHelpers.name,
                                        map(newBodyDamages, (damage, key) =>
                                          key === 0
                                            ? { ...damage, isMain: true }
                                            : damage,
                                        ),
                                      )
                                    }
                                  }
                                }
                                return (
                                  <>
                                    <Box>
                                      {map(
                                        values.bodyConditions[elementIndex]
                                          .bodyDamages,
                                        (damage, damageIndex) => (
                                          <Grid
                                            container
                                            key={damageIndex}
                                            sx={{
                                              mt: damageIndex > 0 && 4,
                                            }}
                                          >
                                            <Grid item xs={12} md={7}>
                                              <Typography
                                                variant="caption"
                                                gutterBottom
                                              >
                                                {t('carEditDamage')}
                                              </Typography>
                                              <Box sx={{ display: 'flex' }}>
                                                <Select
                                                  name={`bodyConditions[${elementIndex}].bodyDamages[${damageIndex}].bodyDamageId`}
                                                  value={damage.bodyDamageId}
                                                  fullWidth
                                                  onChange={handleChange}
                                                >
                                                  {map(
                                                    bodyDamageDictionary,
                                                    ({ name, value }, key) => (
                                                      <MenuItem
                                                        key={key}
                                                        value={value}
                                                        disabled={isOptionSelected(
                                                          values.bodyConditions[
                                                            elementIndex
                                                          ].bodyDamages,
                                                          value,
                                                        )}
                                                        sx={{
                                                          display:
                                                            value === 1
                                                              ? 'none'
                                                              : 'flex',
                                                        }}
                                                      >
                                                        {name}
                                                      </MenuItem>
                                                    ),
                                                  )}
                                                </Select>
                                                <Tooltip
                                                  arrow
                                                  title="Usuń uszkodzenie"
                                                >
                                                  <IconButton
                                                    disableFocusRipple
                                                    disableRipple
                                                    size="small"
                                                    onClick={handleDamageRemove(
                                                      damageIndex,
                                                    )}
                                                    sx={({ palette }) => ({
                                                      borderRadius: 0,
                                                      color:
                                                        palette.primary
                                                          .contrastText,
                                                      backgroundColor:
                                                        palette.primary.main,
                                                    })}
                                                  >
                                                    <ClearIcon />
                                                  </IconButton>
                                                </Tooltip>
                                              </Box>
                                            </Grid>
                                            <Grid
                                              item
                                              xs={12}
                                              md={2}
                                              sx={{
                                                display: {
                                                  xs: 'block',
                                                  md: 'flex',
                                                },
                                                justifyContent: 'center',
                                                mt: {
                                                  xs: 2,
                                                  md: 0,
                                                },
                                              }}
                                            >
                                              <Typography
                                                variant="caption"
                                                gutterBottom
                                                sx={{
                                                  display: {
                                                    xs: 'block',
                                                    md: 'none',
                                                  },
                                                }}
                                              >
                                                {t(
                                                  'Wybierz jako główne uszkodzenie do oceny',
                                                )}
                                              </Typography>
                                              <Box sx={{ display: 'flex' }}>
                                                <Tooltip
                                                  arrow
                                                  title="Wybierz jako główne uszkodzenie do oceny"
                                                >
                                                  <FormControl
                                                    sx={{
                                                      justifyContent: 'center',
                                                      mt: {
                                                        md: 2,
                                                      },
                                                    }}
                                                  >
                                                    <Radio
                                                      name={`bodyConditions[${elementIndex}].bodyDamages[${damageIndex}].isMain`}
                                                      value={
                                                        values.bodyConditions[
                                                          elementIndex
                                                        ].bodyDamages[
                                                          damageIndex
                                                        ].isMain
                                                      }
                                                      checked={
                                                        values.bodyConditions[
                                                          elementIndex
                                                        ].bodyDamages[
                                                          damageIndex
                                                        ].isMain
                                                      }
                                                      sx={{ py: 1, px: 2 }}
                                                      onChange={
                                                        handleMainDamageChange
                                                      }
                                                    />
                                                  </FormControl>
                                                </Tooltip>
                                              </Box>
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                              <Typography
                                                variant="caption"
                                                gutterBottom
                                              >
                                                {t('carEditDamagePhoto')}
                                              </Typography>
                                              <Box>
                                                {isNull(
                                                  values.bodyConditions[
                                                    elementIndex
                                                  ].bodyDamages[damageIndex]
                                                    .image,
                                                ) ? (
                                                  <Button
                                                    variant="contained"
                                                    component="label"
                                                    startIcon={<PhotoCamera />}
                                                    fullWidth
                                                    sx={{ height: 56 }}
                                                  >
                                                    <input
                                                      name={`bodyConditions[${elementIndex}].bodyDamages[${damageIndex}].image`}
                                                      hidden
                                                      accept="image/*"
                                                      type="file"
                                                      onChange={saveBodyConditionImage(
                                                        element.bodyElementId,
                                                        setFieldValue,
                                                      )}
                                                      style={{
                                                        display: 'none',
                                                      }}
                                                    />
                                                  </Button>
                                                ) : (
                                                  <Box sx={{ display: 'flex' }}>
                                                    <Button
                                                      fullWidth
                                                      onClick={handleImageModalOpen(
                                                        element.bodyElementName,
                                                        damage.image.imageUrl,
                                                      )}
                                                      sx={{
                                                        p: 0,
                                                        height: 56,
                                                        backgroundImage: `url(${damage.image.imageUrl})`,
                                                        backgroundSize: 'cover',
                                                        backgroundPosition:
                                                          'center',
                                                      }}
                                                    />
                                                    <Tooltip
                                                      arrow
                                                      title="Usuń zdjęcie"
                                                    >
                                                      <IconButton
                                                        size="small"
                                                        disableFocusRipple
                                                        disableRipple
                                                        sx={({ palette }) => ({
                                                          borderRadius: 0,
                                                          fill: palette.primary
                                                            .contrastText,
                                                          backgroundColor:
                                                            palette.primary
                                                              .main,
                                                        })}
                                                        onClick={deleteBodyConditionImage(
                                                          `bodyConditions[${elementIndex}].bodyDamages[${damageIndex}].image`,
                                                          setFieldValue,
                                                        )}
                                                      >
                                                        <DeleteIcon />
                                                      </IconButton>
                                                    </Tooltip>
                                                  </Box>
                                                )}
                                              </Box>
                                            </Grid>
                                          </Grid>
                                        ),
                                      )}
                                    </Box>
                                    <Button
                                      size="small"
                                      variant="text"
                                      startIcon={<AddIcon fontSize="small" />}
                                      sx={{ mt: 1, p: 1 }}
                                      onClick={() => {
                                        arrayHelpers.push({
                                          bodyDamageId: 1,
                                          isMain: false,
                                          image: null,
                                        })
                                      }}
                                    >
                                      {t('carEditDamageAdd')}
                                    </Button>
                                  </>
                                )
                              }}
                            </FieldArray>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </>
                </FieldArray>
              </Box>
              <Portal container={portalRef.current}>
                <ButtonBox
                  buttons={[
                    {
                      label: 'Wstecz',
                      icon: <ArrowLeft fill="white" />,
                      props: {
                        variant: 'contained',
                        color: 'primary',
                        loading: isSubmitting,
                        onClick: () => {
                          saveBodyCondition({
                            redirect: `/controls/${digitalCertificateId}/edit/reviewHistory`,
                            values,
                            submitForm,
                            setSubmitting,
                          })
                        },
                      },
                    },
                    {
                      label: 'Anuluj',
                      icon: <CancelIcon />,
                      props: {
                        variant: 'outlined',
                        color: 'primary',
                        onClick: onCancelClick,
                      },
                    },
                    {
                      label: 'Zapisz',
                      icon: <SaveIcon fill="white" />,
                      props: {
                        variant: 'contained',
                        color: 'primary',
                        loading: isSubmitting,
                        onClick: () => {
                          saveBodyCondition({
                            redirect: false,
                            values,
                            submitForm,
                            setSubmitting,
                          })
                        },
                      },
                    },
                    {
                      label: 'Zakończ',
                      icon: <CheckIcon fill="white" />,
                      props: {
                        variant: 'contained',
                        color: 'primary',
                        loading: isSubmitting,
                        onClick: () => {
                          saveBodyCondition({
                            redirect: `/controls/${digitalCertificateId}/summary`,
                            values,
                            submitForm,
                            setSubmitting,
                          })
                        },
                      },
                    },
                    {
                      label: t('nextBtnLabel'),
                      icon: <ArrowRight fill="white" />,
                      props: {
                        variant: 'contained',
                        color: 'primary',
                        loading: isSubmitting,
                        onClick: () => {
                          saveBodyCondition({
                            redirect: `/controls/${digitalCertificateId}/edit/interiorCondition`,
                            values,
                            submitForm,
                            setSubmitting,
                          })
                        },
                      },
                    },
                  ]}
                />
              </Portal>
            </Form>
          )}
        </Formik>
      </Section>
      <ImageModal
        title={imageModal.title}
        isOpen={imageModal.isOpen}
        imageUrl={imageModal.imageUrl}
        onClose={handleImageModalClose}
      />
    </div>
  )
}

export default ControlsEditBodyCondition
